import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import PageShell from "@/components/site/PageShell";
import useSEO from "@/lib/useSEO";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { articles, getArticleBySlug } from "@/data/articles";

export default function KnowledgeArticle() {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);
  useSEO({
    title: article ? `${article.title} | IncentTax Knowledge Center` : "Knowledge Center | IncentTax",
    description: article ? article.excerpt : undefined,
    canonical: article ? `https://incenttax.com/knowledge/${article.slug}` : undefined,
  });
  if (!article) return <Navigate to="/knowledge" replace />;

  const related = articles.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <PageShell testid="knowledge-article-page">
      <article>
        {/* HEADER */}
        <section className="bg-forest-900 text-ivory">
          <div className="max-w-4xl mx-auto px-6 sm:px-8 py-20 sm:py-28">
            <Link
              to="/knowledge"
              className="inline-flex items-center gap-2 text-ivory/70 hover:text-brass mb-8 text-sm"
              data-testid="article-back"
            >
              <ArrowLeft size={16} /> Knowledge Center
            </Link>
            <div className="flex items-center gap-4 mb-6 text-xs uppercase tracking-[0.22em] text-brass">
              <span>{article.category}</span>
              <span className="text-ivory/40">·</span>
              <span className="text-ivory/60">{article.readTime}</span>
              <span className="text-ivory/40">·</span>
              <span className="text-ivory/60">{article.publishedOn}</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory leading-[1.06] tracking-tight">
              {article.title}
            </h1>
            <p className="mt-8 text-ivory/85 text-lg sm:text-xl leading-relaxed">
              {article.excerpt}
            </p>
          </div>
        </section>

        {/* BODY */}
        <section className="py-20 bg-ivory">
          <div className="max-w-3xl mx-auto px-6 sm:px-8" data-testid="article-body">
            {article.body.map((block, i) => {
              const key = `${block.type}-${i}-${String(
                Array.isArray(block.content) ? block.content[0] : block.content
              ).slice(0, 32)}`;
              if (block.type === "p")
                return (
                  <p key={key} className="text-charcoal text-lg leading-relaxed mb-6">
                    {block.content}
                  </p>
                );
              if (block.type === "h2")
                return (
                  <h2
                    key={key}
                    className="font-serif text-3xl sm:text-4xl text-forest-900 mt-14 mb-6 leading-tight"
                  >
                    {block.content}
                  </h2>
                );
              if (block.type === "h3")
                return (
                  <h3
                    key={key}
                    className="font-serif text-2xl text-forest-900 mt-10 mb-4"
                  >
                    {block.content}
                  </h3>
                );
              if (block.type === "ul")
                return (
                  <ul key={key} className="space-y-3 mb-8 text-charcoal text-lg leading-relaxed">
                    {block.content.map((item) => (
                      <li key={item} className="flex gap-3">
                        <span className="text-brass shrink-0 mt-1.5">▸</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                );
              if (block.type === "callout")
                return (
                  <div
                    key={key}
                    className="border-l-4 border-brass bg-ivory-200 px-6 py-5 my-8 text-forest-900 italic font-serif text-xl leading-snug"
                  >
                    {block.content}
                  </div>
                );
              return null;
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-forest-900 text-ivory">
          <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
            <BookOpen className="text-brass mx-auto mb-6" size={36} strokeWidth={1.5} />
            <h2 className="font-serif text-3xl sm:text-5xl text-ivory leading-tight mb-6">
              Ready to put this into practice?
            </h2>
            <p className="text-ivory/80 text-lg mb-10 max-w-2xl mx-auto">
              A 30-minute call with one of our sales leaders — free, no obligation. We'll model the impact on your portfolio before you commit to anything.
            </p>
            <Link
              to="/#contact"
              className="inline-flex items-center justify-center gap-2 bg-brass hover:bg-brass-500 text-forest-900 px-10 py-4 font-medium"
              data-testid="article-cta"
            >
              Request Free Review <ArrowRight size={18} />
            </Link>
          </div>
        </section>

        {/* RELATED */}
        {related.length > 0 && (
          <section className="py-20 bg-ivory">
            <div className="max-w-7xl mx-auto px-6 sm:px-8">
              <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">Continue reading</p>
              <h2 className="font-serif text-3xl sm:text-4xl text-forest-900 mb-10">More from the Knowledge Center</h2>
              <div className="grid md:grid-cols-3 gap-px bg-ivory-400 border border-ivory-400">
                {related.map((a, i) => (
                  <Link
                    key={a.slug}
                    to={`/knowledge/${a.slug}`}
                    className="bg-white p-8 hover:bg-ivory-100 transition-colors group"
                    data-testid={`related-article-${i}`}
                  >
                    <div className="text-brass uppercase tracking-[0.22em] text-xs mb-3">{a.category}</div>
                    <h3 className="font-serif text-xl text-forest-900 leading-snug group-hover:text-brass transition-colors mb-3">
                      {a.title}
                    </h3>
                    <span className="inline-flex items-center gap-2 text-brass text-sm">Read <ArrowRight size={14} /></span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </PageShell>
  );
}
